import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback - Handles the OAuth redirect from Emergent Google Auth
 * Processes session_id from URL fragment and exchanges it for a session cookie
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error("No session_id found in URL");
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // Exchange session_id for user data and set cookie
        const response = await axios.post(
          `${API}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const user = response.data;

        // Bridge Google auth into the app's JWT-based dashboard flow.
        // The backend returns an app JWT; store it so DashboardPage and all
        // /api/admin/* calls (which require a Bearer token) authenticate.
        if (user.token) {
          localStorage.setItem("vpp_token", user.token);
          localStorage.setItem("vpp_user", JSON.stringify({
            username: user.email,
            name: user.name,
            email: user.email,
            picture: user.picture,
          }));
          // Persist the Google avatar separately: LoginPage re-verifies the
          // token on the dashboard and overwrites vpp_user (which drops picture).
          if (user.picture) {
            localStorage.setItem("vpp_picture", user.picture);
          }
        }

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);

        // Navigate to dashboard with user data
        navigate("/dashboard", { 
          replace: true,
          state: { user }
        });

      } catch (error) {
        console.error("Auth callback failed:", error);
        navigate("/login", { 
          replace: true,
          state: { error: "Authentication failed. Please try again." }
        });
      }
    };

    processAuth();
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-[#3b82f6] animate-spin mx-auto mb-4" />
        <p className="text-zinc-500 font-body">Completing sign in...</p>
      </div>
    </div>
  );
}
